import wrapper from 'wrapper';
import ResizePlugin from './ResizePlugin';
import SearchLookup from './search-lookup';
import numFormat from './number-formatter';

const config = wrapper.getConfig();
const { language, languageCode, assetsPath } = config;
const { doses, exceptions, maxDosesPer100 } = config.app;

class Lookup {
    constructor(data, target) {
        this.data = data;
        this.target = target;

        this.containerClass = 'country-card';

        this.resultsContent = wrapper.select('.country-card__main-results');

        this.firstVaccination = wrapper.select(`.${this.containerClass}__firstVaccination`);
        this.allDosesGiven = wrapper.select(`.${this.containerClass}__all`);
        this.totalDosesGiven = wrapper.select(`.${this.containerClass}__total`);
        this.firstDosesPer100 = wrapper.select(`.${this.containerClass}__dosesPer`);
        this.vaccinesInUseHeader = wrapper.select(`.${this.containerClass}__vaccinesInUseHeader`);
        this.vaccinesInUse = wrapper.select(`.${this.containerClass}__vaccinesInUse`);
        this.lastUpdated = wrapper.select(`.${this.containerClass}__lastUpdated`);
        this.footnote = wrapper.select('.results__footer-note');

        this.progressBar = wrapper.select('.results__doses-per-100-bar');
        this.progressBarAllDoses = wrapper.select('.results__all-doses-per-100-bar');
        this.countryFlag = wrapper.select('.results__flag');
        this.countryName = wrapper.select('.results__country-name');

        this.noResultsView = wrapper.select('.country-card__no-results');
        this.resultsView = wrapper.select('.country-card__results');
    }

    formatDoses(d) {
        if (d < 1.0) return __('below-one');
        return numFormat.format(language, Math.round(Number(Number(d))));
    }

    singularOrPlural(num, vocabId) {
        let vocab = Number(num) === 1 ? `${vocabId}-singular` : vocabId;

        if (language === 'portuguese' && Number(num) < 2) { // Anything below 2 is singular in Portuguese
            vocab = `${vocabId}-singular`;
        }

        return vocab;
    }

    calculateBarWidth(value, total) {
        return (value * 100 / total);
    }

    populateData(resultData) {
        const width = resultData.firstDosesPer100;
        const totalDoses = numFormat.format(language, resultData.totalDoses);
        const firstDosesPer100 = this.formatDoses(resultData.firstDosesPer100);
        const dosesVocab = this.singularOrPlural(resultData.firstDosesPer100, 'first-doses-per-100');
        const fullyVaccinated = language.match(/nepali|persian/) ? this.formatDoses(resultData.fullDosesPer100) : numFormat.format(language, this.formatDoses(resultData.fullDosesPer100));

        this.firstVaccination.innerHTML = __('first-vaccination').replace('{{startDate}}', resultData.startDate);
        this.totalDosesGiven.innerHTML = __('doses-total').replace('{{number}}', `<span class="results__data results__data--total">${totalDoses}</span>`);

        this.firstDosesPer100.innerHTML = __(dosesVocab)
            .replace('{{number}}', `<span class="results__data results__data-per-100">${firstDosesPer100}</span>`)
            .replace('{{percent}}', `<span class="results__data results__data-per-100">${__('percent')}</span>`);

        console.log("__('all-doses-per-100')", __('all-doses-per-100'));
        const { vocab } = wrapper.getConfig();
        console.log(vocab);

        this.allDosesGiven.innerHTML = __('all-doses-per-100')
            .replace('{{number}}', `<span class="results__data results__data--total">${fullyVaccinated}</span>`)
            .replace('{{percent}}', `<span class="results__data results__data--total">${__('percent')}</span>`);

        this.vaccinesInUseHeader.innerHTML = __('vaccines-in-use').replace('{{country}}', resultData.name);
        this.vaccinesInUse.innerHTML = this.createVaxList(resultData);

        const timeout = this.resultsContent.className.indexOf('hidden') === -1 ? 0 : 100;
        setTimeout(() => {
            this.progressBarAllDoses.style.width = `${this.calculateBarWidth(resultData.fullDosesPer100, 100)}%`;
            this.progressBar.style.width = `${this.calculateBarWidth(resultData.firstDosesPer100, 100)}%`;
        }, timeout);
    }

    setLastUpdated(lastUpdated) {
        if (lastUpdated && lastUpdated !== 'NA') {
            this.lastUpdated.innerHTML = __('last-updated').replace('{{date}}', lastUpdated);
        }
        else {
            this.lastUpdated.innerHTML = '';
        }
    }

    createVaxList(data) {
        return data.vax_codes.map(code => {
            // Check if vaccine has name exception by composing the exception id from
            // vaccine code, language and ISO and checking if that exists in the exceptions list
            const exceptionId = `${code}_${language}_${data.id}`;
            const vaccineName = exceptions[exceptionId] || __(code);

            const vocabId = this.singularOrPlural(doses[code], 'vaccine-doses');
            const vaccineDoses = __(vocabId).replace('{{number}}', doses[code]);
            return `<li>${vaccineName} ${vaccineDoses}</li>`;
        }).join('');
    }

    showNoResults() {
        this.noResultsView.style.display = 'block';
        this.resultsView.style.display = 'none';
        this.footnote.innerHTML = '';
    }

    showResults() {
        this.resultsView.style.display = 'block';
        this.noResultsView.style.display = 'none';
        this.footnote.innerHTML = __('disclaimer-card');
    }

    updateCountryHeading(id, name) {
        const { excludedFlags } = wrapper.getConfig().app;

        this.countryName.innerHTML = name;
        this.countryFlag.src = '';
        this.countryFlag.alt = '';

        if (excludedFlags.indexOf(id) !== -1) {
            this.countryFlag.style.display = 'none';
        }
        else {
            this.countryFlag.alt = __('country-flag').replace('{{country}}', name);
            this.countryFlag.src = `${assetsPath}/img/flags/${id}.svg`;
            this.countryFlag.style.display = 'inline';
        }
    }

    minChars() {
        if (language === 'korean' || language === 'japanese') return -1;
        return 2;
    }

    init() {
        new SearchLookup({
            target: this.target,
            data: this.data,
            clearInput: true,
            confirmOnBlur: false,
            minChars: this.minChars(),
            pattern: '[0-9a-zA-Z ]{2,}',
            onSelect: (id, data) => {
                wrapper.trackClick('search-click'); // Tracks how many times a user has clicked the search
                wrapper.trackUserAction('get-country', id); // Tracks which countries were searched, reporting back ISO code

                this.updateCountryHeading(id, data.name);

                if (data && data.startDate !== 'NA') { // Check if vaccination has started
                    this.populateData(data);
                    this.setLastUpdated(data.lastUpdated);
                    this.showResults();
                }
                else {
                    this.setLastUpdated(data.lastUpdated);
                    this.showNoResults();
                }

                this.resultsContent.classList.remove('hidden');
            },
            overrideResults: (query, results) => {
                return results.sort((a, b) => a.data.name.localeCompare(b.data.name, languageCode, { ignorePunctuation: true }));
            },
            noResultsCallback: (e) => {
                e.preventDefault(); return false;
            },
            plugins: [
                new ResizePlugin({
                    container: wrapper.select('#main-lookup-container'),
                    bufferSize: 30,
                }),
            ],
        });
    }
}

export default Lookup;
